import {
  KIND_COLORS,
  alertKind,
  type AffectedAsset,
  type Asset,
  type AlertEvaluation,
  type ComponentStatus,
  type PollingMode,
  type Role,
  type TenantAlert,
} from '@ww/shared';

/** Asset status colours from the mockup (`ST`). */
export type AssetStatusKey = 'inside' | 'near' | 'advisory' | 'watch' | 'clear' | 'disabled';
export interface AssetStatus {
  key: AssetStatusKey;
  color: string;
  label: string;
  rank: number;
  /** "~15-25 min" when the soonest alert carries a usable storm track. */
  etaLabel?: string | null;
}
export const ST: Record<AssetStatusKey, AssetStatus> = {
  inside: { key: 'inside', color: '#ff7a7f', label: 'In warning', rank: 4 },
  near: { key: 'near', color: '#ffa857', label: 'Near warning', rank: 3 },
  advisory: { key: 'advisory', color: '#4fd6ef', label: 'In advisory', rank: 2 },
  watch: { key: 'watch', color: '#f7cf4f', label: 'In watch area', rank: 1 },
  clear: { key: 'clear', color: '#78b7ff', label: 'Clear', rank: 0 },
  disabled: { key: 'disabled', color: '#6b7a92', label: 'Disabled', rank: -1 },
};

/** Worst status per asset across all active tenant alerts. */
export function computeAssetStatuses(assets: Asset[], alerts: TenantAlert[]): Map<string, AssetStatus> {
  const out = new Map<string, AssetStatus>();
  const nearest = new Map<string, number>();
  const soonest = new Map<string, [number, number]>();
  for (const a of assets) out.set(a._id, a.enabled ? ST.clear : ST.disabled);
  for (const ta of alerts) {
    const ev = ta.evaluation;
    if (!ev || !Array.isArray(ev.affectedAssets)) continue;
    const category = ev.category ?? ta.alert?.category;
    for (const aa of ev.affectedAssets) {
      const cur = out.get(aa.assetId);
      if (!cur || cur.key === 'disabled') continue;
      let next: AssetStatusKey;
      if (category === 'warning') next = aa.relation === 'inside' ? 'inside' : 'near';
      else if (category === 'watch') next = 'watch';
      else next = 'advisory';
      if (next === 'near') {
        const d = aa.distanceMiles;
        const prev = nearest.get(aa.assetId);
        if (typeof d === 'number' && aa.relation === 'proximity' && (prev === undefined || d < prev)) nearest.set(aa.assetId, d);
      }
      // Soonest arrival across every alert touching this asset. Absent whenever the
      // alert carries no storm track — there is no fallback estimate on purpose.
      const eta = aa.etaRangeMinutes;
      if (Array.isArray(eta) && eta.length === 2) {
        const prevEta = soonest.get(aa.assetId);
        if (!prevEta || eta[0] < prevEta[0]) soonest.set(aa.assetId, [eta[0], eta[1]]);
      }
      if (ST[next].rank > cur.rank) out.set(aa.assetId, ST[next]);
    }
  }
  for (const [id, st] of out) {
    const d = nearest.get(id);
    const eta = soonest.get(id);
    const label = st.key === 'near' && d !== undefined ? `${d.toFixed(1)} mi from warning` : st.label;
    const etaLabel = eta ? `~${eta[0]}–${eta[1]} min` : null;
    if (label !== st.label || etaLabel) out.set(id, { ...st, label, etaLabel });
  }
  return out;
}

export function relationLabel(rel: AffectedAsset['relation'], category?: string): string {
  if (rel === 'inside') return category === 'warning' ? 'Asset in warning' : 'Inside area';
  if (rel === 'proximity') return 'Within radius';
  return 'County';
}

/** "Courthouse (Asset in warning), Jail (Within radius) +2 more" */
/**
 * Names of the extra alert locations that brought this alert in, when the coverage area
 * itself was not hit; null otherwise.
 */
export function watchAreaLabel(ev: AlertEvaluation | null | undefined): string | null {
  if (!ev || ev.countyAffected) return null;
  const labels = (ev.matchedAreas ?? []).filter((a) => a.id !== null).map((a) => a.label);
  return labels.length ? labels.join(', ') : null;
}

export function affectedSummary(ev: AlertEvaluation | null | undefined, max = 3): string {
  if (!ev) return 'Not evaluated for this tenant';
  const list = Array.isArray(ev.affectedAssets) ? ev.affectedAssets : [];
  if (!list.length) {
    if (ev.countyAffected) return 'County-level · no assets affected';
    const via = watchAreaLabel(ev);
    return via ? `Via ${via} · no assets affected` : 'No monitored assets';
  }
  const shown = list.slice(0, max).map((a) => `${a.name} (${relationLabel(a.relation, ev.category)})`);
  const more = list.length > max ? ` +${list.length - max} more` : '';
  return shown.join(', ') + more;
}

/** "2 inside · 1 within radius" */
export function affectedCounts(ev: AlertEvaluation | null | undefined): string {
  if (!ev) return '—';
  const list = Array.isArray(ev.affectedAssets) ? ev.affectedAssets : [];
  const inside = list.filter((a) => a.relation === 'inside').length;
  const near = list.filter((a) => a.relation === 'proximity').length;
  const zone = list.filter((a) => a.relation === 'zone').length;
  const parts: string[] = [];
  if (inside) parts.push(`${inside} inside`);
  if (near) parts.push(`${near} within radius`);
  if (zone) parts.push(`${zone} in county`);
  return parts.length ? parts.join(' · ') : 'No monitored assets';
}

export function kindStyle(event: string) {
  const kind = alertKind(event ?? '');
  return { kind, ...KIND_COLORS[kind] };
}

export const COMPONENT_COLORS: Record<ComponentStatus, string> = {
  ok: '#3fb950',
  degraded: '#ffb547',
  down: '#ff6b70',
  unknown: '#6b7a92',
};
export const COMPONENT_LABELS: Record<ComponentStatus, string> = {
  ok: 'OK',
  degraded: 'Degraded',
  down: 'Down',
  unknown: 'Unknown',
};
export function componentColor(s: ComponentStatus | null | undefined): string {
  return COMPONENT_COLORS[s ?? 'unknown'] ?? COMPONENT_COLORS.unknown;
}

export const OVERALL_LABELS: Record<ComponentStatus, string> = {
  ok: 'All systems operational',
  degraded: 'Degraded',
  down: 'System outage',
  unknown: 'Status unknown',
};

export const COMPONENT_NAMES = {
  nws: 'NWS Alerts API',
  radar: 'Radar provider',
  mongo: 'MongoDB',
  dragonfly: 'Dragonfly cache',
  worker: 'Ingestion worker',
  sms: 'SMS adapter',
  lightning: 'Lightning (GOES)',
  realtime: 'Realtime (SSE)',
} as const;
export type ComponentKey = keyof typeof COMPONENT_NAMES;
export const COMPONENT_KEYS = Object.keys(COMPONENT_NAMES) as ComponentKey[];

export interface ModeStyle {
  label: string;
  name: string;
  color: string;
  bg: string;
  border: string;
  pulse: boolean;
}
export const MODE_STYLES: Record<PollingMode, ModeStyle> = {
  normal: { label: 'NORMAL', name: 'Normal mode', color: '#8cc2ff', bg: 'rgba(90,169,255,.10)', border: '#26426b', pulse: false },
  heightened: { label: 'HEIGHTENED', name: 'Heightened mode', color: '#f7cf4f', bg: 'rgba(245,197,24,.12)', border: '#65561f', pulse: false },
  alert: { label: 'ALERT MODE', name: 'Alert mode', color: '#ff8f93', bg: 'rgba(255,90,95,.12)', border: '#6e2a31', pulse: true },
  recovery: { label: 'RECOVERY', name: 'Recovery mode', color: '#ffa857', bg: 'rgba(255,140,26,.12)', border: '#6b4520', pulse: false },
};
export const UNKNOWN_MODE: ModeStyle = {
  label: 'POLLING UNKNOWN',
  name: 'Unknown',
  color: '#9fb0c8',
  bg: 'rgba(107,122,146,.12)',
  border: '#2a3850',
  pulse: false,
};
export function modeStyle(mode: PollingMode | null | undefined): ModeStyle {
  return mode ? MODE_STYLES[mode] ?? UNKNOWN_MODE : UNKNOWN_MODE;
}

export const NOTIFICATION_COLORS: Record<string, string> = {
  sent: '#6fdc98',
  logged: '#8cc2ff',
  suppressed: '#9fb0c8',
  failed: '#ff8f93',
  none: '#9fb0c8',
  not_required: '#9fb0c8',
};
export const NOTIFICATION_LABELS: Record<string, string> = {
  sent: 'Sent',
  logged: 'Logged (no webhook)',
  suppressed: 'Suppressed',
  failed: 'Failed',
  none: 'Not sent',
  not_required: 'Dashboard only',
};

export const ROLE_LABELS: Record<Role, string> = {
  super_admin: 'Super admin',
  tenant_admin: 'Tenant admin',
  dispatcher: 'Dispatcher',
  viewer: 'Viewer',
};

/**
 * May administer this county: rules, assets, coverage, users, settings.
 *
 * An allow-list on purpose. The deny-list form (`role !== 'viewer'`) admits every role
 * added later, at compile time and without a test failing — which is exactly what would
 * have happened to `dispatcher`.
 */
export function isAdmin(role: Role | null | undefined): boolean {
  return role === 'super_admin' || role === 'tenant_admin';
}

/** May post and clear road closures. Administrators can do anything a dispatcher can. */
export function canEditClosures(role: Role | null | undefined): boolean {
  return role === 'dispatcher' || isAdmin(role);
}

/** Common NWS event names offered in rule/asset forms. */
export const COMMON_EVENTS = [
  'Tornado Warning',
  'Severe Thunderstorm Warning',
  'Flash Flood Warning',
  'Flood Warning',
  'Tornado Watch',
  'Severe Thunderstorm Watch',
  'Flood Advisory',
  'Special Weather Statement',
  'Winter Storm Warning',
  'Ice Storm Warning',
  'Blizzard Warning',
  'Snow Squall Warning',
  'Winter Storm Watch',
  'Winter Weather Advisory',
  'High Wind Warning',
];

export function eventOptions(extra: string[] | null | undefined): string[] {
  const out = [...COMMON_EVENTS];
  for (const e of extra ?? []) if (e && !out.includes(e)) out.push(e);
  return out;
}

export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1]?.[0] ?? '' : '')).toUpperCase();
}

export function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  return String(e);
}
